import { useState, useMemo } from 'react';

/**
 * Normaliza un string: minúsculas y sin acentos
 * Ejemplo: "Córdoba" → "cordoba"
 */
const normalizeText = text =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Verifica si las palabras del filtro coinciden con palabras consecutivas de la descripción
 * Ejemplo: "daniel g" coincide con "Ampliación Daniel Garza"
 * @param {string} description - Texto de la opción
 * @param {string} filter - Texto de búsqueda
 */
const matchesFilter = (description, filter) => {
  const normalizedFilter = normalizeText(filter).trim();
  if (!normalizedFilter) return true;

  const descriptionWords = normalizeText(description).split(/\s+/);
  const filterWords = normalizedFilter.split(/\s+/);

  // Recorre cada posición posible de inicio en la descripción
  // y verifica si todas las palabras del filtro coinciden consecutivamente
  return descriptionWords.some((_, startIndex) => {
    const remainingWords = descriptionWords.length - startIndex;
    if (remainingWords < filterWords.length) return false;

    return filterWords.every((filterWord, i) => descriptionWords[startIndex + i].startsWith(filterWord));
  });
};

/**
 * Custom hook para manejar el filtrado de opciones en un select
 * @param {Array} options - Opciones disponibles del select
 */
const useFilterOptions = options => {
  const [filterText, setFilterText] = useState('');

  const filteredOptions = useMemo(() => {
    const trimmedFilter = filterText.trim();
    if (!trimmedFilter) return options;
    return options.filter(option => matchesFilter(option.description, filterText));
  }, [options, filterText]);

  const showNoResults = filterText.trim() && filteredOptions.length === 0;

  const handleFilterChange = e => setFilterText(e.target.value);

  const clearFilter = () => setFilterText('');

  return {
    filterText,
    filteredOptions,
    showNoResults,
    handleFilterChange,
    clearFilter
  };
};

export default useFilterOptions;
